import { Component, inject, OnInit, signal, computed } from '@angular/core'
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router'
import { FormsModule } from '@angular/forms'
import { TagModule } from 'primeng/tag'
import { CardModule } from 'primeng/card'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { DividerModule } from 'primeng/divider'
import { InputTextModule } from 'primeng/inputtext'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { OptimizationDebugService } from '../../services/optimization-debug.service'
import {
  AffairOptimizationRunResponse,
  AffairOptimizationResult,
} from '../../../interventions/services/interventions.service'

/** Déplacement inter-jours d'une intervention programmée (tour_data.day_shift_moves). */
interface DayShiftMoveView {
  name: string
  originalDate: string | null
  newDate: string | null
  newStart: string | null
  postponablePeriod: 'day' | 'week' | 'month' | null
}

/** Créneau proposé dont la tournée implique un changement de jour. */
interface DayShiftProposalView {
  affairId: number
  affairName: string
  slotId: number
  rank: number
  date: string
  technicianName: string | null
  moves: DayShiftMoveView[]
}

@Component({
  selector: 'app-optimization-run-detail',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    DecimalPipe,
    FormsModule,
    TagModule,
    CardModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    DividerModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
  ],
  templateUrl: './optimization-run-detail.component.html',
  styleUrl: './optimization-run-detail.component.scss',
})
export class OptimizationRunDetailComponent implements OnInit {
  private service = inject(OptimizationDebugService)
  private route = inject(ActivatedRoute)
  private router = inject(Router)

  public detailLoading$ = this.service.detailLoading$

  run = signal<AffairOptimizationRunResponse | null>(null)
  expandedAffairId: number | null = null
  expandedTourDataSlotIds = new Set<number>()
  searchQuery = ''

  get filteredAffairs(): AffairOptimizationResult[] {
    const detail = this.run()
    if (!detail) return []

    const sorted = [...detail.affairs].sort(
      (a, b) => (a.efficiencyRank ?? 999) - (b.efficiencyRank ?? 999)
    )

    const q = this.searchQuery.trim().toLowerCase()
    if (!q) return sorted

    return sorted.filter(
      (a) =>
        a.affairName.toLowerCase().includes(q) ||
        (a.postalCode ?? '').toLowerCase().includes(q) ||
        (a.locality ?? '').toLowerCase().includes(q) ||
        (a.groupingName ?? '').toLowerCase().includes(q) ||
        String(a.affairId).includes(q)
    )
  }

  /**
   * Tous les créneaux du run dont la proposition implique de déplacer des
   * interventions programmées sur un autre jour (passe globale).
   */
  get dayShiftProposals(): DayShiftProposalView[] {
    const detail = this.run()
    if (!detail) return []

    const proposals: DayShiftProposalView[] = []
    for (const affair of detail.affairs) {
      for (const slot of affair.slots) {
        if (slot.tourData?.requires_day_shift !== true) continue
        const rawMoves = slot.tourData?.day_shift_moves
        proposals.push({
          affairId: affair.affairId,
          affairName: affair.affairName,
          slotId: slot.id,
          rank: slot.rank,
          date: slot.date,
          technicianName: slot.technicianName ?? null,
          moves: Array.isArray(rawMoves)
            ? rawMoves.map((m: any) => ({
                name: m.name ?? 'Intervention',
                originalDate: m.original_date ?? null,
                newDate: m.new_date ?? null,
                newStart: m.new_start ?? null,
                postponablePeriod: m.postponable_period ?? null,
              }))
            : [],
        })
      }
    }
    return proposals.sort(
      (a, b) => a.date.localeCompare(b.date) || a.rank - b.rank
    )
  }

  /** Ouvre l'affaire concernée dans la liste et scrolle jusqu'à elle. */
  goToProposalAffair(proposal: DayShiftProposalView) {
    this.searchQuery = ''
    this.expandedAffairId = proposal.affairId
    this.expandedTourDataSlotIds.add(proposal.slotId)
    setTimeout(() => {
      document
        .getElementById('affair-row-' + proposal.affairId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  postponableLabel(period: 'day' | 'week' | 'month' | null): string {
    switch (period) {
      case 'day':
        return 'journée'
      case 'week':
        return 'semaine'
      case 'month':
        return 'mois'
      default:
        return '—'
    }
  }

  ngOnInit() {
    const runId = Number(this.route.snapshot.paramMap.get('runId'))
    if (!runId) {
      this.goBack()
      return
    }
    this.service.getRunDetail(runId).subscribe((detail) => {
      this.run.set(detail)
    })
  }

  goBack() {
    this.router.navigate(['/admin/optimization-debug'])
  }

  toggleAffair(affairId: number) {
    this.expandedAffairId = this.expandedAffairId === affairId ? null : affairId
  }

  toggleTourData(slotId: number) {
    if (this.expandedTourDataSlotIds.has(slotId)) {
      this.expandedTourDataSlotIds.delete(slotId)
    } else {
      this.expandedTourDataSlotIds.add(slotId)
    }
  }

  formatTourData(tourData: any): string {
    return JSON.stringify(tourData, null, 2)
  }

  clearSearch() {
    this.searchQuery = ''
    this.expandedAffairId = null
  }

  totalSlotsCount(): number {
    return this.run()?.affairs.reduce((sum, a) => sum + a.slots.length, 0) ?? 0
  }

  getStatusSeverity(
    status: string
  ): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (status) {
      case 'completed':
        return 'success'
      case 'running':
        return 'info'
      case 'pending':
        return 'warn'
      case 'failed':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'completed':
        return 'Terminé'
      case 'running':
        return 'En cours'
      case 'pending':
        return 'En attente'
      case 'failed':
        return 'Échoué'
      default:
        return status
    }
  }

  formatServiceDuration(seconds: number | null | undefined): string {
    if (!seconds) return '—'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    return h > 0 ? `${h}h${m > 0 ? m + 'min' : ''}` : `${m}min`
  }

  coveragePercent(): number | null {
    const r = this.run()
    if (!r?.affairsCount || !r?.affairsWithSlotsCount) return null
    return Math.round((r.affairsWithSlotsCount / r.affairsCount) * 100)
  }
}
